<template>
  <el-dialog
    v-model="visible"
    :title="isEdit ? '부조금 수정' : '부조금 등록'"
    :width="isMobile ? '95%' : '600px'"
    destroy-on-close
    @close="handleClose"
  >
    <el-form ref="formRef" :model="form" :rules="rules" label-width="100px" size="default">
      <el-row :gutter="16">
        <el-col :span="isMobile ? 24 : 12">
          <el-form-item label="구분" prop="direction">
            <el-radio-group v-model="form.direction">
              <el-radio-button value="GIVEN">지출 (낸)</el-radio-button>
              <el-radio-button value="RECEIVED">수입 (받은)</el-radio-button>
            </el-radio-group>
          </el-form-item>
        </el-col>
        <el-col :span="isMobile ? 24 : 12">
          <el-form-item label="유형" prop="record_type">
            <el-radio-group v-model="form.record_type">
              <el-radio-button value="MONEY">부조금</el-radio-button>
              <el-radio-button value="FLOWER">화환</el-radio-button>
            </el-radio-group>
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="16">
        <el-col :span="isMobile ? 24 : 12">
          <el-form-item label="경조사유형" prop="event_type">
            <el-select v-model="form.event_type" placeholder="선택" style="width: 100%">
              <el-option label="결혼" value="WEDDING" />
              <el-option label="장례" value="FUNERAL" />
              <el-option label="출산" value="BIRTH" />
              <el-option label="돌잔치" value="BIRTHDAY" />
              <el-option label="기타" value="OTHER" />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="isMobile ? 24 : 12">
          <el-form-item label="경조사일" prop="event_date">
            <el-date-picker
              v-model="form.event_date"
              type="date"
              placeholder="날짜 선택"
              value-format="YYYY-MM-DD"
              style="width: 100%"
            />
          </el-form-item>
        </el-col>
      </el-row>

      <el-form-item label="이벤트명" prop="event_name">
        <el-input v-model="form.event_name" placeholder="예: 홍길동 부친상, 김철수 결혼" />
      </el-form-item>

      <el-row :gutter="16">
        <el-col :span="isMobile ? 24 : 12">
          <el-form-item label="이름" prop="person_name">
            <el-input v-model="form.person_name" placeholder="대상자 이름" />
          </el-form-item>
        </el-col>
        <el-col :span="isMobile ? 24 : 12">
          <el-form-item label="업체명">
            <el-input v-model="form.company_name" placeholder="업체명/소속" />
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="16">
        <el-col :span="isMobile ? 24 : 12">
          <el-form-item label="관계">
            <el-input v-model="form.relationship" placeholder="거래처/지인/직원가족 등" />
          </el-form-item>
        </el-col>
        <el-col :span="isMobile ? 24 : 12">
          <el-form-item label="금액" prop="amount">
            <el-input-number
              v-model="form.amount"
              :min="0"
              :step="10000"
              :controls="true"
              style="width: 100%"
            />
          </el-form-item>
        </el-col>
      </el-row>

      <el-form-item v-if="form.record_type === 'FLOWER'" label="화환종류">
        <el-input v-model="form.flower_description" placeholder="화환 종류/설명" />
      </el-form-item>

      <el-form-item label="비고">
        <el-input v-model="form.memo" type="textarea" :rows="2" placeholder="메모" />
      </el-form-item>
    </el-form>

    <template #footer>
      <div class="dialog-footer">
        <el-button v-if="isEdit" type="danger" plain @click="handleDelete" style="margin-right: auto">
          삭제
        </el-button>
        <el-button @click="visible = false">취소</el-button>
        <el-button type="primary" @click="handleSubmit" :loading="saving">
          {{ isEdit ? '수정' : '저장' }}
        </el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script setup>
import { ref, reactive, watch, computed } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import axiosInstance from '@/api/axios'

const props = defineProps({
  modelValue: Boolean,
  record: { type: Object, default: () => ({}) },
  defaultDirection: {
    type: String,
    default: 'GIVEN',
  },
})

const emit = defineEmits(['update:modelValue', 'saved'])

const visible = ref(false)
const saving = ref(false)
const formRef = ref(null)
const isMobile = ref(window.innerWidth <= 768)

const isEdit = computed(() => !!props.record?.id)

const defaultForm = () => ({
  direction: props.defaultDirection || 'GIVEN',
  record_type: 'MONEY',
  event_type: '',
  event_name: '',
  event_date: '',
  person_name: '',
  company_name: '',
  relationship: '',
  amount: 0,
  flower_description: '',
  memo: '',
})

const form = reactive(defaultForm())

const rules = {
  direction: [{ required: true, message: '구분을 선택하세요', trigger: 'change' }],
  record_type: [{ required: true, message: '유형을 선택하세요', trigger: 'change' }],
  event_type: [{ required: true, message: '경조사유형을 선택하세요', trigger: 'change' }],
  event_name: [{ required: true, message: '이벤트명을 입력하세요', trigger: 'blur' }],
  event_date: [{ required: true, message: '경조사일을 선택하세요', trigger: 'change' }],
  person_name: [{ required: true, message: '이름을 입력하세요', trigger: 'blur' }],
  amount: [{ required: true, message: '금액을 입력하세요', trigger: 'change' }],
}

watch(() => props.modelValue, (val) => {
  visible.value = val
  if (val) {
    if (props.record) {
      Object.assign(form, {
        direction: props.record.direction || 'GIVEN',
        record_type: props.record.record_type || 'MONEY',
        event_type: props.record.event_type || '',
        event_name: props.record.event_name || '',
        event_date: props.record.event_date || '',
        person_name: props.record.person_name || '',
        company_name: props.record.company_name || '',
        relationship: props.record.relationship || '',
        amount: Number(props.record.amount) || 0,
        flower_description: props.record.flower_description || '',
        memo: props.record.memo || '',
      })
    } else {
      Object.assign(form, defaultForm())
    }
  }
})

watch(visible, (val) => {
  emit('update:modelValue', val)
})

const handleSubmit = async () => {
  try {
    await formRef.value.validate()
  } catch {
    return
  }

  saving.value = true
  try {
    if (isEdit.value) {
      await axiosInstance.put(`/condolences/${props.record.id}/`, form)
      ElMessage.success('수정되었습니다')
    } else {
      await axiosInstance.post('/condolences/', form)
      ElMessage.success('등록되었습니다')
    }
    visible.value = false
    emit('saved')
  } catch (error) {
    console.error('Save failed:', error)
  } finally {
    saving.value = false
  }
}

const handleDelete = async () => {
  try {
    await ElMessageBox.confirm('이 내역을 삭제하시겠습니까?', '삭제 확인', {
      confirmButtonText: '삭제',
      cancelButtonText: '취소',
      type: 'warning',
    })
    await axiosInstance.delete(`/condolences/${props.record.id}/`)
    ElMessage.success('삭제되었습니다')
    visible.value = false
    emit('saved')
  } catch (error) {
    if (error !== 'cancel') {
      console.error('Delete failed:', error)
    }
  }
}

const handleClose = () => {
  visible.value = false
}
</script>

<style scoped>
.dialog-footer {
  display: flex;
  justify-content: flex-end;
}
</style>
